import React, { forwardRef, useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils.js";

/* ---------------- ResizeHandle ----------------
   The keyboard-operable window splitter (a focusable WAI-ARIA separator) for
   one panel. It owns no width: `value` is the panel's size in px, and
   `onChange` gets the next size, rounded and clamped to [min, max]. Pair it
   with `useResizableWidth`. `edge` is the panel side the handle sits on: the
   arrow key that moves that edge outward grows the panel, mirrored under RTL.
   A drag uses pointer capture and the delta from pointerdown. The hit area
   and paint of each variant are fixed; `className` places the handle only.
   Focus is the shared `focus-ring`, and teal appears nowhere else. */

export type ResizeHandleVariant = "line" | "grip";
/** `ai` is for Player AI surfaces only. */
export type ResizeHandleTone = "neutral" | "ai";

/* Set by the part itself, so a caller cannot pass one that would be ignored. */
type OwnedProps =
  | "children"
  | "role"
  | "tabIndex"
  | "onChange"
  | `on${"KeyDown" | "LostPointerCapture"}`
  | `onPointer${"Down" | "Move" | "Up" | "Cancel"}`
  | `aria-${"orientation" | "label" | "controls" | `value${"min" | "max" | "now" | "text"}`}`;

export interface ResizeHandleProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  OwnedProps
> {
  /** Accessible name, e.g. "Resize rail". */
  label: string;
  /** The panel's current size in px. */
  value: number;
  /** Smallest size in px; Home jumps here. */
  min: number;
  /** Largest size in px; End jumps here. */
  max: number;
  /** Receives the next size in px, rounded and clamped to [min, max]. */
  onChange: (next: number) => void;
  /** Arrow-key step in px (default 8). Shift moves 4x the step. */
  step?: number;
  /** The panel side the handle sits on (default "start"). */
  edge?: "start" | "end";
  /** "line" (default): a 44px hit area straddling the panel edge around a
   *  4px line. "grip": a 16px in-flow hit area around a 48px pill. */
  variant?: ResizeHandleVariant;
  /** Hover and drag paint (default "neutral"). */
  tone?: ResizeHandleTone;
  /** id of the panel it resizes (aria-controls). */
  controls?: string;
  /** true on pointerdown; false on pointerup, cancel and unmount. */
  onResizingChange?: (resizing: boolean) => void;
}

/* Rest geometry copies today's hand-rolled separators: "line" is
   WorkspaceRightRail's (transparent at rest), "grip" ExportReportDialog's
   (a pb-border-strong pill at rest). */
const LINE_EDGE = { start: "start-[-22px]", end: "end-[-22px]" } as const;
const GRIP_ROOT =
  "group flex w-4 shrink-0 cursor-col-resize touch-none items-center justify-center rounded focus-ring";
const MARK: Record<ResizeHandleVariant, string> = {
  line: "pointer-events-none absolute inset-y-0 inset-x-5 w-1",
  grip: "h-12 w-1 rounded-full bg-pb-border-strong",
};
/* Hover and drag (data-resizing) paint. Literal strings so Tailwind sees them. */
const PAINT: Record<ResizeHandleVariant, Record<ResizeHandleTone, string>> = {
  line: {
    neutral:
      "group-hover:bg-pb-border-strong group-data-[resizing]:bg-pb-border-strong",
    ai: "group-hover:bg-pb-iris group-data-[resizing]:bg-pb-iris",
  },
  grip: {
    neutral:
      "group-hover:bg-pb-text-muted group-data-[resizing]:bg-pb-text-muted",
    ai: "group-hover:bg-pb-iris group-data-[resizing]:bg-pb-iris",
  },
};

/** +1 when a move to the physical right grows the panel, else -1. */
function growSign(element: Element, edge: "start" | "end"): 1 | -1 {
  const dir = element.closest("[dir]")?.getAttribute("dir");
  const rtl = (dir ?? window.getComputedStyle(element).direction) === "rtl";
  return (edge === "end") !== rtl ? 1 : -1;
}

export const ResizeHandle = forwardRef<HTMLDivElement, ResizeHandleProps>(
  function ResizeHandle(
    {
      label,
      value,
      min,
      max,
      onChange,
      step = 8,
      edge = "start",
      variant = "line",
      tone = "neutral",
      controls,
      onResizingChange,
      className,
      ...rest
    },
    ref,
  ) {
    const clamp = (n: number): number =>
      Math.min(max, Math.max(min, Math.round(n)));
    const now = clamp(value);
    const drag = useRef<{ x: number; value: number; sign: 1 | -1 } | null>(
      null,
    );
    const [resizing, setResizing] = useState(false);

    // The unmount cleanup below reads the latest callback, not the first one.
    const resizingChange = useRef(onResizingChange);
    useEffect(() => {
      resizingChange.current = onResizingChange;
    });
    useEffect(
      () => () => {
        if (drag.current) resizingChange.current?.(false);
        drag.current = null;
      },
      [],
    );

    const endDrag = (): void => {
      if (!drag.current) return;
      drag.current = null;
      setResizing(false);
      onResizingChange?.(false);
    };

    /* eslint-disable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions -- the ARIA window-splitter pattern makes role=separator focusable and keyboard-operable */
    return (
      <div
        ref={ref}
        {...rest}
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        aria-controls={controls}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={now}
        aria-valuetext={`${now} pixels`}
        tabIndex={0}
        data-resizing={resizing ? "" : undefined}
        onKeyDown={(event) => {
          let next: number | undefined;
          if (event.key === "Home") next = min;
          if (event.key === "End") next = max;
          if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
            const delta =
              (event.shiftKey ? step * 4 : step) *
              growSign(event.currentTarget, edge);
            next = now + (event.key === "ArrowRight" ? delta : -delta);
          }
          if (next === undefined) return;
          event.preventDefault();
          onChange(clamp(next));
        }}
        onPointerDown={(event) => {
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          // A press during a drag restarts it from here; it stays one drag.
          if (!drag.current) {
            setResizing(true);
            onResizingChange?.(true);
          }
          const sign = growSign(event.currentTarget, edge);
          drag.current = { x: event.clientX, value: now, sign };
        }}
        onPointerMove={(event) => {
          const start = drag.current;
          if (start)
            onChange(
              clamp(start.value + start.sign * (event.clientX - start.x)),
            );
        }}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        className={cn(
          variant === "line"
            ? `group absolute inset-y-0 ${LINE_EDGE[edge]} z-10 w-11 touch-none cursor-col-resize focus-ring`
            : GRIP_ROOT,
          className,
        )}
      >
        <span aria-hidden className={cn(MARK[variant], PAINT[variant][tone])} />
      </div>
    );
    /* eslint-enable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */
  },
);
